import { Injectable, inject, signal } from '@angular/core';

import { GISGO_AUTH_CONTEXT } from '@gisgo/shared';

import { DriversApi } from '../api/drivers-api';
import { OpsApi } from '../api/ops-api';
import { OrderUpdatedEvent } from '../api/ops-orders.models';
import { EchoService } from './echo';
import { OrderChime } from './order-chime';

/**
 * Compteurs live de la sidebar (badge Incidents — spec §2.4). Alimenté par
 * GET /ops/incidents + les events `ops.incident.{opened,updated}`.
 * Réservé dispatcher/admin (le canal incidents refuse sales).
 */
@Injectable({ providedIn: 'root' })
export class OpsBadges {
  private readonly api = inject(OpsApi);
  private readonly driversApi = inject(DriversApi);
  private readonly echoService = inject(EchoService);
  private readonly auth = inject(GISGO_AUTH_CONTEXT);
  private readonly chime = inject(OrderChime);

  readonly openIncidents = signal(0);
  readonly pendingKyc = signal(0);
  readonly ordersToValidate = signal(0);
  private readonly seenOrderIds = new Set<number>();
  private connected = false;

  connect(): void {
    const role = this.auth.role();
    if (this.connected || (role !== 'dispatcher' && role !== 'admin')) {
      return;
    }
    this.connected = true;
    this.refresh();
    this.refreshKyc();

    const channel = this.echoService.connect().private('ops.incidents');
    channel.listen('.ops.incident.opened', () => this.refresh());
    channel.listen('.ops.incident.updated', () => this.refresh());

    this.refreshOrders();
    const orders = this.echoService.connect().private('ops.orders');
    orders.listen('.ops.order.updated', (event: OrderUpdatedEvent) => {
      if (event.status === 'placed' && !this.seenOrderIds.has(event.order_id)) {
        this.seenOrderIds.add(event.order_id);
        this.chime.play();
      }
      this.refreshOrders();
    });
  }

  refresh(): void {
    this.api.incidents().subscribe({
      next: (incidents) => this.openIncidents.set(incidents.length),
      error: () => this.openIncidents.set(0),
    });
  }

  refreshKyc(): void {
    this.driversApi.drivers().subscribe({
      next: (drivers) =>
        this.pendingKyc.set(drivers.filter((driver) => driver.status === 'kyc_pending').length),
      error: () => this.pendingKyc.set(0),
    });
  }

  /** Commandes gérées en attente de validation (spec 2026-08-25 §7.1). */
  refreshOrders(): void {
    this.api.orders({ status: ['placed'] }).subscribe({
      next: (page) => {
        for (const order of page.data) {
          this.seenOrderIds.add(order.id);
        }
        this.ordersToValidate.set(page.meta.counts.to_validate);
      },
      error: () => this.ordersToValidate.set(0),
    });
  }
}
